import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { mergeAttributes } from '@tiptap/core';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { HighlightColor } from '@narrativebench/editor-core';
import { DocumentHeader } from './DocumentHeader';
import { FloatingTocButton } from './FloatingTocButton';
import { FloatingToc } from './FloatingToc';
import { CommentSidebar, CommentSidebarProvider } from './CommentSidebar';
import { ExportDocument } from './ExportDocument';
import { SidebarInset } from './ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePersistedTocPinned } from '../hooks/usePersistedTocPinned';
import { findScrollContainer } from '../utils/editor-utils';
import { createStaticEditorExtensions } from '../utils/editor-extensions';
import { applyDocumentApiContentToEditor, isUsableEditor } from '../utils/document-api-yjs';
import { DocumentLoadingPanel } from './DocumentLoadingPanel';
import { APP_VIEWPORT_HEIGHT } from '../constants/layout';
import { findActiveTocItemPos, findActiveTocItemPosAtDocPos, TOC_ACTIVE_VIEWPORT_OFFSET_PX, TOC_ACTIVE_VIEWPORT_SLOP_PX } from '../utils/document-section-navigation';
import { extractTocEntries, type TocEntry } from '../utils/extract-toc-entries';

const HighlightColorDisabled = HighlightColor.extend({
  renderHTML({ HTMLAttributes }) {
    const { style, ...restAttributes } = HTMLAttributes;
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, restAttributes),
      0,
    ];
  },
});

export const ReadOnlyDocument: React.FC = () => {
  const { projectId, documentId } = useParams<{ projectId?: string; documentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useAuth();
  const { t } = useLocale();
  const shareToken = searchParams.get('shareToken');
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentIcon, setDocumentIcon] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tocHeadings, setTocHeadings] = useState<TocEntry[]>([]);
  const [tocParagraphs, setTocParagraphs] = useState<Array<{ text: string; pos: number }>>([]);
  const [isFloatingTocVisible, setIsFloatingTocVisible] = useState<boolean>(false);
  const [isTocPinned, setIsTocPinned] = usePersistedTocPinned(documentId);
  const [activeTocPos, setActiveTocPos] = useState<number | null>(null);
  const ignoreTocScrollActiveUpdatesRef = useRef<boolean>(false);
  const [draggedTocIndex, setDraggedTocIndex] = useState<number | null>(null);
  const [dragOverTocIndex, setDragOverTocIndex] = useState<number | null>(null);
  const [touchDraggedIndex, setTouchDraggedIndex] = useState<number | null>(null);
  const [touchDragOverIndex, setTouchDragOverIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const tocListRef = useRef<HTMLUListElement | null>(null);
  const isMobile = useIsMobile();
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);

  const editor = useEditor({
    extensions: createStaticEditorExtensions(HighlightColorDisabled),
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'tiptap prose prose-sm sm:prose lg:prose-lg xl:prose-2xl dark:prose-invert prose-p:text-foreground prose-headings:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-blockquote:text-foreground mx-auto w-full focus:outline-none min-h-[400px]',
      },
    },
  });
  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEffect(() => {
    if (!authLoading && !token && !shareToken) {
      navigate('/login');
    }
  }, [authLoading, token, shareToken, navigate]);

  useEffect(() => {
    const loadDocument = async (): Promise<void> => {
      if (authLoading || !isUsableEditor(editor)) {
        return;
      }

      if (!documentId || documentId === 'new') {
        setIsLoading(false);
        return;
      }

      if (shareToken) {
        setIsLoading(true);
        setError(null);

        try {
          const response = await fetch(`${API_BASE}/api/share-links/${shareToken}/document`);

          if (!response.ok) {
            if (response.status === 404) {
              setError(t('document_not_found') || 'Document not found');
            } else {
              setError(t('failed_to_load_document') || 'Failed to load document');
            }
            setIsLoading(false);
            return;
          }

          const data = await response.json();

          setDocumentTitle(data.title || '');
          setDocumentIcon(data.icon || null);
          setOwnerEmail(data.ownerEmail || null);

          applyDocumentApiContentToEditor(data, editorRef.current);

          setIsLoading(false);
        } catch (err) {
          console.error('Error loading document via share token:', err);
          setError(t('failed_to_load_document') || 'Failed to load document');
          setIsLoading(false);
        }
        return;
      }

      if (!token) {
        setError(t('no_permission') || "You don't have permission to access this page.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const documentUrl = projectId && projectId !== 'new'
          ? `${API_BASE}/api/documents/${documentId}?contextProjectId=${encodeURIComponent(projectId)}`
          : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(documentUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            setError(t('no_permission') || "You don't have permission to access this page.");
          } else if (response.status === 404) {
            setError(t('document_not_found') || 'Document not found');
          } else {
            setError(t('failed_to_load_document') || 'Failed to load document');
          }
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        if (!data.sharePermission || data.sharePermission !== 'reader') {
          navigate(projectId ? `/projects/${projectId}/documents/${documentId}` : `/documents/${documentId}`);
          return;
        }

        setDocumentTitle(data.title || '');
        setDocumentIcon(data.icon || null);
        setOwnerEmail(data.ownerEmail || null);

        const headerProjectId = projectId || data.projectId;
        if (headerProjectId) {
          try {
            const projectResponse = await fetch(`${API_BASE}/api/projects/${headerProjectId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            if (projectResponse.ok) {
              const projectData = await projectResponse.json();
              setProjectTitle(projectData.title || '');
            }
          } catch (err) {
            console.error('Error fetching project title:', err);
          }
        }

        applyDocumentApiContentToEditor(data, editorRef.current);

        setIsLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(t('failed_to_load_document') || 'Failed to load document');
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [token, documentId, editor, projectId, shareToken, t, navigate, authLoading]);

  useEffect(() => {
    if (!editor) return undefined;

    const extractHeadingsAndParagraphs = (): void => {
      const { headings, paragraphs } = extractTocEntries(editor.state.doc);
      setTocHeadings(headings);
      setTocParagraphs(paragraphs);
    };

    extractHeadingsAndParagraphs();
    if (isFloatingTocVisible || isTocPinned) {
      editor.on('update', extractHeadingsAndParagraphs);
      return () => {
        editor.off('update', extractHeadingsAndParagraphs);
      };
    }

    return undefined;
  }, [editor, isFloatingTocVisible, isTocPinned]);

  useEffect(() => {
    if (!editor || (!isFloatingTocVisible && !isTocPinned)) {
      return undefined;
    }

    const getTocItems = () => (tocHeadings.length > 0 ? tocHeadings : tocParagraphs);

    const updateActiveTocPosFromScroll = (): void => {
      if (ignoreTocScrollActiveUpdatesRef.current) {
        return;
      }
      const scrollContainer = findScrollContainer(editor.view.dom.parentElement);
      const viewportTop = (scrollContainer?.getBoundingClientRect().top ?? 0) + TOC_ACTIVE_VIEWPORT_OFFSET_PX;
      setActiveTocPos(
        findActiveTocItemPos(
          getTocItems(),
          (pos) => {
            try {
              return editor.view.coordsAtPos(Math.min(pos + 1, editor.state.doc.content.size)).top;
            } catch {
              return null;
            }
          },
          viewportTop,
          TOC_ACTIVE_VIEWPORT_SLOP_PX,
        ),
      );
    };

    const finishProgrammaticTocScroll = (): void => {
      ignoreTocScrollActiveUpdatesRef.current = false;
      updateActiveTocPosFromScroll();
    };

    const updateActiveTocPosFromSelection = (): void => {
      setActiveTocPos(findActiveTocItemPosAtDocPos(getTocItems(), editor.state.selection.from));
    };

    updateActiveTocPosFromScroll();
    const scrollContainer = findScrollContainer(editor.view.dom.parentElement);
    scrollContainer?.addEventListener('scroll', updateActiveTocPosFromScroll, { passive: true });
    scrollContainer?.addEventListener('scrollend', finishProgrammaticTocScroll);
    editor.on('selectionUpdate', updateActiveTocPosFromSelection);

    return () => {
      scrollContainer?.removeEventListener('scroll', updateActiveTocPosFromScroll);
      scrollContainer?.removeEventListener('scrollend', finishProgrammaticTocScroll);
      editor.off('selectionUpdate', updateActiveTocPosFromSelection);
    };
  }, [editor, isFloatingTocVisible, isTocPinned, tocHeadings, tocParagraphs]);

  const scrollToTocPosition = (pos: number): void => {
    if (!editor) return;

    ignoreTocScrollActiveUpdatesRef.current = true;
    setActiveTocPos(pos);

    const { view } = editor;
    const coords = view.coordsAtPos(pos + 1);
    const scrollContainer = findScrollContainer(view.dom.parentElement);

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetY = coords.top - containerRect.top + scrollContainer.scrollTop - TOC_ACTIVE_VIEWPORT_OFFSET_PX;
      scrollContainer.scrollTo({
        top: targetY,
        behavior: 'smooth',
      });
      window.setTimeout(() => {
        if (ignoreTocScrollActiveUpdatesRef.current) {
          ignoreTocScrollActiveUpdatesRef.current = false;
        }
      }, 600);
    } else {
      window.scrollTo({
        top: coords.top + window.scrollY - TOC_ACTIVE_VIEWPORT_OFFSET_PX,
        behavior: 'smooth',
      });
      ignoreTocScrollActiveUpdatesRef.current = false;
    }
  };

  const moveTocSection = (): void => {
    // No-op in read-only mode
  };

  if (authLoading || isLoading) {
    return <DocumentLoadingPanel />;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={() => navigate('/desk')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--scribane-black)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          {t('back_to_desk') || 'Back to Desk'}
        </button>
      </div>
    );
  }

  const toggleExport = (): void => {
    setIsExportOpen((previous) => !previous);
  };

  const closeExport = (): void => {
    setIsExportOpen(false);
  };

  return (
    <CommentSidebarProvider defaultOpen={false}>
      <div style={{ minHeight: APP_VIEWPORT_HEIGHT, width: '100%', backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
        <SidebarInset style={{ overflow: 'hidden', height: APP_VIEWPORT_HEIGHT }}>
          <div style={{ display: 'flex', height: APP_VIEWPORT_HEIGHT, width: '100%', overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row', position: 'relative' }}>
            <FloatingTocButton
              tocHeadings={tocHeadings}
              isFloatingTocVisible={isFloatingTocVisible}
              setIsFloatingTocVisible={setIsFloatingTocVisible}
              isTocPinned={isTocPinned}
              setIsTocPinned={setIsTocPinned}
              isMobile={isMobile}
              isFocusMode={false}
              hasMainAreaView={isExportOpen}
              t={t}
            />
            {!isExportOpen && (
              <FloatingToc
                tocHeadings={tocHeadings}
                tocParagraphs={tocParagraphs}
                isFloatingTocVisible={isFloatingTocVisible}
                setIsFloatingTocVisible={setIsFloatingTocVisible}
                isTocPinned={isTocPinned}
                setIsTocPinned={setIsTocPinned}
                activeTocPos={activeTocPos}
                isMobile={isMobile}
                isReadOnly={true}
                scrollToTocPosition={scrollToTocPosition}
                moveTocSection={moveTocSection}
                draggedTocIndex={draggedTocIndex}
                setDraggedTocIndex={setDraggedTocIndex}
                dragOverTocIndex={dragOverTocIndex}
                setDragOverTocIndex={setDragOverTocIndex}
                touchDraggedIndex={touchDraggedIndex}
                setTouchDraggedIndex={setTouchDraggedIndex}
                touchDragOverIndex={touchDragOverIndex}
                setTouchDragOverIndex={setTouchDragOverIndex}
                touchStartY={touchStartY}
                setTouchStartY={setTouchStartY}
                tocListRef={tocListRef}
                t={t}
              />
            )}
            <div style={{ flex: 1, width: '100%', overflow: 'auto', paddingTop: '0', paddingBottom: '2rem', position: 'relative', backgroundColor: 'var(--background)' }}>
              <DocumentHeader
                documentTitle={documentTitle}
                projectTitle={projectTitle}
                documentIcon={documentIcon}
                onDocumentTitleChange={() => {}}
                onProjectTitleChange={() => {}}
                onIconChange={() => {}}
                isReadOnly={true}
                showFocusMode={false}
                ownerEmail={ownerEmail}
              />
              {isExportOpen ? (
                <div style={{ width: '100%', maxWidth: '100%', margin: '0 auto', paddingLeft: '2rem', paddingRight: '2rem', paddingTop: '2rem', boxSizing: 'border-box' }}>
                  <ExportDocument
                    editor={editor}
                    ydoc={null}
                    documentTitle={documentTitle}
                    projectTitle={projectTitle}
                    documentId={documentId}
                    token={token}
                    comments={[]}
                    suggestions={[]}
                    t={t}
                    hideHeading={false}
                    onClose={closeExport}
                  />
                </div>
              ) : (
                <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto', paddingLeft: '2rem', paddingRight: '2rem', boxSizing: 'border-box' }}>
                  <div style={{ minHeight: '400px', width: '100%', padding: '1rem 0' }}>
                    {editor && <EditorContent editor={editor} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </SidebarInset>
        <CommentSidebar
          showComments={false}
          showSuggestions={false}
          showExport={true}
          isExportActive={isExportOpen}
          onExportClick={toggleExport}
          t={t}
        />
      </div>
    </CommentSidebarProvider>
  );
};

